import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { CharacterCard, CharacterCardSkeleton } from '../components/CharacterCard';
import { Search, Sparkles, Star, Wand2, Gamepad2, Scroll, Mic, UserPlus, Loader2, Heart, Rocket, Skull, Laugh, TrendingUp, Tag, Users, Crown, MessageCircle } from 'lucide-react';
import BoostedRail from '../components/BoostedRail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  0: '#71717A', 1: '#A1A1AA', 2: '#60A5FA', 3: '#34D399', 4: '#FBBF24',
  5: '#F59E0B', 6: '#F97316', 7: '#EF4444', 8: '#E11D48', 9: '#A855F7', 10: '#E11D48',
};

const categoryIcons = {
  anime: Sparkles, fantasy: Wand2, romance: Heart, gaming: Gamepad2,
  historical: Scroll, music: Mic, custom: UserPlus, scifi: Rocket,
  horror: Skull, comedy: Laugh
};

export default function Discover() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [characters, setCharacters] = useState([]);
  const [topMonthly, setTopMonthly] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMonthly, setLoadingMonthly] = useState(true);
  const [searchTab, setSearchTab] = useState('characters');
  const [userResults, setUserResults] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchTopMonthly();
  }, []);

  useEffect(() => {
    fetchCharacters();
    if (searchQuery.trim()) {
      fetchUsers();
    } else {
      setUserResults([]);
    }
  }, [selectedCategory, searchQuery, selectedTag]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories`);
      setCategories(data);
    } catch (err) { console.error(err); }
  };

  const fetchTags = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tags`);
      setAvailableTags(data);
    } catch (err) { console.error(err); }
  };

  const fetchTopMonthly = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/top-monthly`);
      setTopMonthly(data);
    } catch (err) { console.error(err); }
    setLoadingMonthly(false);
  };

  const fetchUsers = async () => {
    if (!searchQuery.trim()) return;
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/users/search`, { params: { q: searchQuery } });
      setUserResults(data);
    } catch (err) { console.error(err); }
    setLoadingUsers(false);
  };

  const fetchCharacters = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      if (selectedTag) params.search = selectedTag;
      const { data } = await axios.get(`${API_URL}/api/characters`, { params });
      // Filter by tag if selected
      if (selectedTag) {
        setCharacters(data.filter(c => c.tags?.some(t => t.toLowerCase() === selectedTag.toLowerCase())));
      } else {
        setCharacters(data);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedTag(''); }}
            placeholder="Search characters..."
            className="pl-10 bg-[#1A1D24] border-0 text-white placeholder:text-[#71717A] rounded-xl text-sm"
            data-testid="discover-search"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-4 pb-1">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.id] || Star;
            return (
              <button
                key={cat.id}
                onClick={() => { setSelectedCategory(cat.id); setSelectedTag(''); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-[#E11D48] text-white'
                    : 'bg-[#1A1D24] text-[#A1A1AA] hover:bg-[#252830]'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-3 h-3" />{cat.name}
              </button>
            );
          })}
        </div>

        {/* Tags / Genres */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-3.5 h-3.5 text-[#F59E0B]" />
            <h3 className="text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider">Tags & Genres</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availableTags.slice(0, 30).map((tag) => (
              <button
                key={tag}
                onClick={() => { setSelectedTag(selectedTag === tag ? '' : tag); setSearchQuery(''); }}
                className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${
                  selectedTag === tag
                    ? 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40'
                    : 'bg-white/5 text-[#71717A] border border-white/5 hover:text-[#A1A1AA] hover:bg-white/10'
                }`}
                data-testid={`discover-tag-${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Boosted Right Now Rail */}
        <BoostedRail />

        {/* Top Monthly Characters */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#E11D48]" />
            <h2 className="text-sm font-bold text-white">Top Monthly Characters</h2>
          </div>
          {loadingMonthly ? (
            <div className="grid grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => <CharacterCardSkeleton key={i} />)}
            </div>
          ) : topMonthly.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {topMonthly.slice(0, 8).map((character) => (
                <CharacterCard key={character.id} character={character} />
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-[#71717A] py-4">No top characters this month yet</p>
          )}
        </section>

        {/* Search/Filter Results */}
        {(searchQuery || selectedTag || selectedCategory !== 'all') && (
          <section>
            {/* Tabs for Characters / Users (only when searching) */}
            {searchQuery && (
              <div className="flex border-b border-white/10 mb-3">
                <button
                  onClick={() => setSearchTab('characters')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${searchTab === 'characters' ? 'text-white' : 'text-[#71717A]'}`}
                  data-testid="search-tab-characters"
                >
                  Characters
                  {searchTab === 'characters' && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />}
                </button>
                <button
                  onClick={() => setSearchTab('users')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${searchTab === 'users' ? 'text-white' : 'text-[#71717A]'}`}
                  data-testid="search-tab-users"
                >
                  Users
                  {searchTab === 'users' && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />}
                </button>
              </div>
            )}

            {/* Characters results */}
            {(searchTab === 'characters' || !searchQuery) && (
              <>
                <h2 className="text-sm font-bold text-white mb-3">
                  {selectedTag ? `Tagged: ${selectedTag}` : searchQuery ? `Results for "${searchQuery}"` : 'Characters'}
                </h2>
                {loading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {[...Array(4)].map((_, i) => <CharacterCardSkeleton key={i} />)}
                  </div>
                ) : characters.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {characters.map((character) => (
                      <CharacterCard key={character.id} character={character} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-[#71717A]">No characters found</p>
                  </div>
                )}
              </>
            )}

            {/* Users results */}
            {searchTab === 'users' && searchQuery && (
              <div>
                <h2 className="text-sm font-bold text-white mb-3">Users matching "{searchQuery}"</h2>
                {loadingUsers ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#E11D48] animate-spin" />
                  </div>
                ) : userResults.length > 0 ? (
                  <div className="space-y-2">
                    {userResults.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => navigate(`/user/${u.id}`)}
                        className="w-full flex items-center gap-3 p-3 bg-[#1A1D24] rounded-xl border border-white/5 hover:bg-[#252830] transition-colors text-left"
                        data-testid={`user-result-${u.id}`}
                      >
                        <div className="w-12 h-12 rounded-full bg-[#0F1115] overflow-hidden flex-shrink-0 border border-white/10">
                          {u.avatar ? (
                            <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-[#71717A]" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white truncate">{u.name}</span>
                            {u.badge && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#E11D48] text-white rounded-full">
                                <Crown className="w-2.5 h-2.5" /> {u.badge}
                              </span>
                            )}
                          </div>
                          {u.bio && <p className="text-xs text-[#71717A] truncate mt-0.5">{u.bio}</p>}
                          <p className="text-[10px] text-[#555] mt-0.5">{u.character_count} characters</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-[#71717A]">No users found</p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
